import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  filterStudentsByProfession, 
  filterStudentsByUniversity,
  filterStudentsByCity,
  searchStudents,
  filterStudentsByGraduateYear
} from '../services/StudentService';
import Navbar from '../components/Navbar';
import '../styles/UserListPage.css';
import defaultProfile from '../assets/default-profile.png';
import Footer from '../components/Footer';

function UserListPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const pageSize = 10;

  // Kullanıcı listesini normalize et (studentId varsa onu kullan, yoksa id)
  const normalizeUserList = (users) => {
    return users.map((user) => ({
      ...user,
      studentId: user.studentId || user.id,
    }));
  };

  // Kullanıcıları yükleme fonksiyonu (URL'den page parametresi okuyup API'ye gönderiyor)
  const loadUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const searchParams = new URLSearchParams(location.search);
      const searchTerm = searchParams.get('search');
      const university = searchParams.get('university');
      const profession = searchParams.get('profession');
      const city = searchParams.get('city');
      const graduateYear = searchParams.get('graduateYear'); // ✅ mezuniyet yılı parametresi

      const pageFromUrl = parseInt(searchParams.get('page')) || 1;

      let response;

       if (searchTerm) {
      response = await searchStudents(searchTerm, pageFromUrl);
    } else if (university) {
      response = await filterStudentsByUniversity(university, pageFromUrl);
    } else if (profession) {
      response = await filterStudentsByProfession(profession, pageFromUrl);
    } else if (city) {
      response = await filterStudentsByCity(city, pageFromUrl);
    } else if (graduateYear) { 
      response = await filterStudentsByGraduateYear(graduateYear, pageFromUrl);
    } else {
      // Eğer filtre yoksa ana sayfaya yönlendir
      navigate('/');
      return;
    }

      if (response.isSuccess) {
        const normalizedUsers = normalizeUserList(response.data);
        setUsers(normalizedUsers);
        setTotalResults(normalizedUsers.length > 0 ? normalizedUsers[0].totalResult : 0);
        setCurrentPage(pageFromUrl);
      } else {
        setError(response.failMessage || 'Kullanıcılar yüklenemedi.');
        setUsers([]);
        setTotalResults(0);
      }
    } catch (err) {
      if (
        err.message.toLowerCase().includes('unauthorized') ||
        err.message.includes('401')
      ) {
        navigate('/login');
      } else {
        setError(err.message || 'Bir hata oluştu.');
      }
      setUsers([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  // location.search değiştiğinde kullanıcıları yeniden yükle
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Sayfa değiştirme fonksiyonu (URL parametresini günceller)
  const goToPage = (pageNum) => {
    const totalPages = Math.ceil(totalResults / pageSize);
    if (pageNum < 1 || pageNum > totalPages) return;

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', pageNum);

    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  // Sayfalama bileşeni
  const Pagination = () => {
    const totalPages = Math.ceil(totalResults / pageSize);
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={i === currentPage ? 'active-page' : ''}
          aria-current={i === currentPage ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button 
          disabled={currentPage === 1} 
          onClick={() => goToPage(currentPage - 1)}
          aria-label="Önceki sayfa"
        >
          «
        </button>
        {pages}
        <button 
          disabled={currentPage === totalPages} 
          onClick={() => goToPage(currentPage + 1)}
          aria-label="Sonraki sayfa"
        >
          »
        </button>
      </div>
    );
  };

  // Kullanıcı kartına tıklayınca profiline git
  const handleUserClick = (userId) => {
    navigate(`/visit-profile-id/${userId}`);
  };

  return (
    <>
      <Navbar />
      <div className="page-wrapper">
        <main className="page-content">
          <div className="user-list-container">
            <h2>Mezunlar</h2>

            {error && <p className="error">{error}</p>}

            {loading ? (
              <div className="loading-spinner" aria-live="polite">
                <i className="fas fa-spinner fa-spin"></i> Yükleniyor...
              </div>
            ) : (
              <>
                <div className="user-cards">
                  {users.length > 0 ? (
                    users.map((user) => (
                      <div 
                        key={user.studentId} 
                        className="user-card"
                        onClick={() => handleUserClick(user.studentId)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if(e.key === 'Enter') handleUserClick(user.studentId); }}
                        aria-label={`${user.firstName} ${user.lastName} profilini görüntüle`}
                      >
                        <div className="user-avatar">
                          <img 
                            src={user.profilePhotoUrl || defaultProfile} 
                            alt={`${user.firstName} ${user.lastName}`}
                            loading="lazy"
                          />
                        </div>
                        <div className="user-info">
                          <h3>{user.firstName} {user.lastName}</h3>
                          <p><i className="fas fa-briefcase"></i> {user.profession || 'Belirtilmemiş'}</p>
                          <p><i className="fas fa-university"></i> {user.universityName || 'Belirtilmemiş'}</p>
                          <p><i className="fas fa-map-marker-alt"></i> {user.city || 'Belirtilmemiş'}</p>
                          <p className="user-bio">{user.bio || 'Merhaba! Ben bu platformun bir üyesiyim.'}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-users">Kullanıcı bulunamadı.</p>
                  )}
                </div>

                <Pagination />
              </>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}

export default UserListPage;
